import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";

function daysLeft(isoDate) {
  if (!isoDate) return 0;
  const diff = new Date(isoDate) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function hoursLeft(isoDate) {
  if (!isoDate) return 0;
  return Math.max(0, Math.round((new Date(isoDate) - new Date()) / (1000 * 60 * 60)));
}

/**
 * Dashboard listing card: stats (views, clicks), share link, QR code, featured slot promo/countdown.
 */
export default function DashboardListingCard({ listing, onDelete }) {
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef(null);

  const publicUrl = `${window.location.origin}/annunci/${listing.id}`;
  const fs = listing.featured_slot; // {expires_at, ...} | null

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiato negli appunti");
    } catch { toast.error("Impossibile copiare"); }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: listing.title, url: publicUrl }); } catch { /* user dismissed */ }
    } else { copyLink(); }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png", 1.0);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hostmarket-qr-${listing.id.slice(0, 8)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR code scaricato (1024×1024px)");
  };

  const del = async () => {
    if (!window.confirm("Eliminare questo annuncio?")) return;
    try {
      await api.delete(`/listings/${listing.id}`);
      toast.success("Annuncio eliminato");
      if (onDelete) onDelete(listing.id);
    } catch { toast.error("Errore eliminazione"); }
  };

  const clicks = listing.clicks || { email: 0, phone: 0, whatsapp: 0, website: 0, total: 0 };

  return (
    <div className="hm-card p-5 space-y-4" data-testid={`my-listing-${listing.id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="font-heading font-bold text-lg">{listing.title}</div>
          <div className="text-xs text-zinc-500 mt-1 flex gap-3 flex-wrap">
            <span>{listing.category_name}</span>
            <span>· Stato: <span className={listing.status === "active" ? "text-[#22C55E]" : "text-zinc-400"}>{listing.status}</span></span>
            {fs && (
              <span className="text-[#FFBE00] font-bold"><i className="fas fa-bolt mr-1"></i>In home page</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/annunci/${listing.id}`} target="_blank" className="px-3 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid={`listing-view-${listing.id}`}>Vedi</Link>
          <button onClick={() => nav(`/dashboard/${listing.id}/modifica`)} className="px-3 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid={`listing-edit-${listing.id}`}>Modifica</button>
          <button onClick={() => setExpanded((x) => !x)} className="px-3 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid={`listing-expand-${listing.id}`}>
            <i className={`fas fa-chevron-${expanded ? "up" : "down"} mr-1`}></i> {expanded ? "Chiudi" : "Statistiche"}
          </button>
          {listing.status !== "active" && (
            <Link to="/prezzi" className="px-3 py-2 text-xs uppercase tracking-wider bg-[#E619A8] text-white rounded-md">Attiva</Link>
          )}
          <button onClick={del} className="px-3 py-2 text-xs uppercase tracking-wider border border-red-900 text-red-400 rounded-md hover:border-red-500 hover:text-red-500" data-testid={`listing-delete-${listing.id}`}>Elimina</button>
        </div>
      </div>

      {/* Quick stats row (always visible) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
        <StatTile label="Visite" value={listing.views || 0} icon="fa-eye" color="#FFBE00" testid={`stat-views-${listing.id}`} />
        <StatTile label="Unici" value={listing.unique_views || 0} icon="fa-user" color="#E619A8" testid={`stat-unique-${listing.id}`} />
        <StatTile label="Click email" value={clicks.email} icon="fa-envelope" color="#E619A8" testid={`stat-email-${listing.id}`} />
        <StatTile label="Click tel" value={clicks.phone} icon="fa-phone" color="#FFBE00" testid={`stat-phone-${listing.id}`} />
        <StatTile label="Click WA" value={clicks.whatsapp} icon="fa-whatsapp fab" color="#25D366" testid={`stat-whatsapp-${listing.id}`} />
      </div>

      {expanded && (
        <div className="pt-4 border-t border-zinc-800 space-y-4" data-testid={`listing-expanded-${listing.id}`}>
          {/* Share / Copy link / QR */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[240px] px-3 py-2 rounded-md bg-black/50 border border-zinc-800 text-xs text-zinc-400 truncate font-mono" title={publicUrl}>
              {publicUrl}
            </div>
            <button onClick={copyLink} className="px-3 py-2 text-xs uppercase tracking-wider bg-[#FFBE00] text-black font-bold rounded-md hover:bg-[#E619A8] hover:text-white" data-testid={`listing-copy-link-${listing.id}`}>
              <i className="fas fa-copy mr-1"></i> Copia link
            </button>
            <button onClick={shareLink} className="px-3 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid={`listing-share-${listing.id}`}>
              <i className="fas fa-share-nodes mr-1"></i> Condividi
            </button>
            <button onClick={() => setShowQr((x) => !x)} className="px-3 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid={`listing-qr-toggle-${listing.id}`}>
              <i className="fas fa-qrcode mr-1"></i> {showQr ? "Nascondi QR" : "QR code"}
            </button>
          </div>

          {showQr && (
            <div className="flex flex-col sm:flex-row items-center gap-5 p-5 rounded-md bg-white" data-testid={`listing-qr-${listing.id}`}>
              <div ref={qrRef}>
                <QRCodeCanvas value={publicUrl} size={1024} level="H" includeMargin={true} style={{ width: 180, height: 180 }} />
              </div>
              <div className="flex-1 text-black">
                <div className="font-heading font-black text-lg">QR code ad alta risoluzione</div>
                <p className="text-sm text-zinc-700 mt-1">Scarica il QR in PNG 1024×1024px. Perfetto per biglietti da visita, flyer, adesivi, stampe.</p>
                <button onClick={downloadQr} className="mt-3 px-4 py-2 bg-black text-white text-xs font-bold uppercase tracking-wider rounded-md hover:bg-[#E619A8]" data-testid={`listing-qr-download-${listing.id}`}>
                  <i className="fas fa-download mr-1"></i> Scarica PNG 1024px
                </button>
              </div>
            </div>
          )}

          {/* Featured Slot section: countdown if active, else promo banner */}
          {fs ? (
            <FeaturedCountdown slot={fs} listingId={listing.id} />
          ) : (
            <FeaturedSlotPromo listingId={listing.id} variant="dashboard" animated={true} />
          )}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon, color, testid }) {
  const isBrand = icon.startsWith("fa-whatsapp");
  return (
    <div className="px-3 py-3 rounded-md border border-zinc-800 bg-black/40" data-testid={testid}>
      <i className={`${isBrand ? "fab" : "fas"} ${icon} text-base mb-1`} style={{ color }}></i>
      <div className="font-heading font-black text-2xl leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function FeaturedCountdown({ slot, listingId }) {
  const days = daysLeft(slot.expires_at);
  const hours = hoursLeft(slot.expires_at);
  const isLast24 = days <= 1 && hours <= 24;
  return (
    <div className="rounded-xl p-5 border-2 border-[#FFBE00] bg-gradient-to-r from-[#FFBE00]/10 to-[#E619A8]/10" data-testid="featured-countdown">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#22C55E] text-black text-[10px] font-bold uppercase tracking-wider">
          <i className="fas fa-bolt"></i> In Home Page
        </span>
        <span className="text-xs uppercase tracking-wider text-zinc-300">Annuncio in vetrina</span>
      </div>
      <div className="mt-3 flex items-end gap-6 flex-wrap">
        <div>
          <div className="font-heading font-black text-5xl text-[#FFBE00]" data-testid="featured-countdown-days">{days}</div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{days === 1 ? "giorno restante" : "giorni restanti"}</div>
        </div>
        <div className="text-sm text-zinc-300 flex-1 min-w-[240px]">
          <div>Scade il <strong className="text-white">{new Date(slot.expires_at).toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" })}</strong></div>
          <div className="text-xs text-zinc-500 mt-1">Attivo dal {new Date(slot.started_at).toLocaleDateString("it-IT")}</div>
          {isLast24 && (
            <div className="mt-2 text-xs text-[#E619A8] font-bold"><i className="fas fa-triangle-exclamation mr-1"></i>Ultimo giorno — rinnova ora per restare in home</div>
          )}
        </div>
        <Link to={`/checkout?featured=1&listing_id=${encodeURIComponent(listingId)}`} className="px-5 py-3 rounded-md bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#FFBE00] hover:text-black transition-all" data-testid="featured-countdown-renew">
          <i className="fas fa-rotate-right mr-1"></i> Rinnova
        </Link>
      </div>
    </div>
  );
}
